/**
 * Storefront Component Styles
 *
 * Adaptive theming: surfaces, hairlines, and mutings derive from
 * currentColor via color-mix so the storefront stays legible across
 * any host WP theme and any dashboard surface/ink config. Only accent,
 * typography, and radii honor dashboard/host overrides.
 */

import { ADAPTIVE_TOKENS } from '../theme';

export const STOREFRONT_STYLES = `
  :host {
    ${ADAPTIVE_TOKENS}
    color: inherit;
    opacity: 1 !important;
    font-family: var(--ksp-font-body);
  }

  .ks-storefront {
    width: 100%;
    /* Container queries — grid & header respond to the storefront's own
       width, not the viewport. Essential in WordPress where the content
       column is narrower than the viewport. */
    container-type: inline-size;
    container-name: ks-storefront;
  }

  /* Header */
  .ks-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .ks-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: inherit;
    margin: 0;
  }

  .ks-controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  /* Select dropdowns */
  .ks-select {
    appearance: none;
    background: var(--ksp-card-bg);
    border: 1px solid var(--ksp-hairline);
    border-radius: var(--ksp-radius-md);
    padding: 0.5rem 2rem 0.5rem 0.75rem;
    font-size: 0.875rem;
    color: inherit;
    font-family: inherit;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor' opacity='0.6'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.5rem center;
    background-size: 1rem;
    transition: border-color 180ms ease;
  }

  .ks-select:hover { border-color: var(--ksp-accent); }

  .ks-select:focus {
    outline: none;
    border-color: var(--ksp-accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--ksp-accent) 15%, transparent);
  }

  /* Category tabs */
  .ks-categories {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
  }

  .ks-category-btn {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 500;
    background: transparent;
    border: none;
    color: var(--ksp-muted);
    cursor: pointer;
    border-radius: var(--ksp-radius-sm);
    transition: color 180ms ease, background 180ms ease;
    font-family: inherit;
  }

  .ks-category-btn:hover {
    color: inherit;
    background: var(--ksp-card-bg);
  }

  .ks-category-btn.active {
    color: var(--ksp-accent);
    background: color-mix(in srgb, var(--ksp-accent) 12%, transparent);
  }

  /* Product grid */
  .ks-grid {
    display: grid;
    grid-template-columns: repeat(var(--ks-columns, 4), minmax(0, 1fr));
    gap: var(--ks-spacing, 1.25rem);
  }

  /* Step down column count as the container narrows.
     Typical product-card minimum width ~160-180px so:
       > 920px → configured (up to 4)
       680-920 → 3 cols
       460-680 → 2 cols
       < 460px → 1 col + stacked header */
  @container ks-storefront (max-width: 920px) {
    .ks-grid {
      grid-template-columns: repeat(min(var(--ks-columns, 4), 3), minmax(0, 1fr));
    }
  }

  @container ks-storefront (max-width: 680px) {
    .ks-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }

  @container ks-storefront (max-width: 460px) {
    .ks-grid { grid-template-columns: 1fr; }
    .ks-header {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  /* List layout */
  .ks-list {
    display: flex;
    flex-direction: column;
    gap: var(--ks-spacing, 1.25rem);
  }

  /* Pagination */
  .ks-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 2rem;
  }

  .ks-page-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: var(--ksp-radius-sm);
    border: 1px solid var(--ksp-hairline);
    background: transparent;
    color: inherit;
    font-size: 0.875rem;
    font-family: inherit;
    cursor: pointer;
    transition: border-color 180ms ease, color 180ms ease, background 180ms ease;
  }

  .ks-page-btn:hover:not(:disabled) {
    border-color: var(--ksp-accent);
    color: var(--ksp-accent);
  }

  .ks-page-btn.active {
    background: var(--ksp-accent);
    border-color: var(--ksp-accent);
    color: var(--ksp-accent-ink);
  }

  .ks-page-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Load more button */
  .ks-load-more {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
  }

  .ks-load-more-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 160px;
    padding: 0.75rem 2rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--ksp-accent);
    background: transparent;
    border: 1.5px solid var(--ksp-accent);
    border-radius: var(--ksp-btn-radius);
    cursor: pointer;
    font-family: inherit;
    transition: background 180ms ease, color 180ms ease;
  }

  .ks-load-more-btn:hover:not(:disabled) {
    background: var(--ksp-accent);
    color: var(--ksp-accent-ink);
  }

  .ks-load-more-btn:disabled { opacity: 0.7; cursor: wait; }

  /* Skeleton loading cards */
  .ks-skeleton-card {
    border-radius: var(--ksp-radius-md);
    overflow: hidden;
    border: 1px solid var(--ksp-hairline);
    background: var(--ksp-card-bg);
  }

  .ks-skeleton-card__image {
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 0;
  }

  .ks-skeleton-card__body {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .ks-skeleton-card__body .ks-skeleton,
  .ks-skeleton-card__body .ks-skeleton-text--lg,
  .ks-skeleton-card__body .ks-skeleton-text--sm {
    background: var(--ksp-hairline-strong);
    border-radius: 4px;
    position: relative;
    overflow: hidden;
  }

  .ks-skeleton-card__body .ks-skeleton-text--lg { height: 18px; }
  .ks-skeleton-card__body .ks-skeleton-text--sm { height: 12px; }

  .ks-skeleton-card__body .ks-skeleton-text--lg::after,
  .ks-skeleton-card__body .ks-skeleton-text--sm::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      transparent 0%,
      color-mix(in srgb, currentColor 12%, transparent) 50%,
      transparent 100%
    );
    animation: ks-shimmer 1.5s ease-in-out infinite;
  }

  /* Slotted content */
  ::slotted([slot="header"]) { margin-bottom: 1rem; }
  ::slotted([slot="footer"]) { margin-top: 1rem; }
`;

export default STOREFRONT_STYLES;
